import {MenuItem, TextField} from "@mui/material";
import React, {Component} from "react";
import {v4 as uuidv4} from "uuid";
import {withAppContext} from "../util/withAppContext";
import spinner from "../assets/Spinner-1s-200px.svg";
import {prepareBuffer} from "../util/Buffer";
import enterpriseComponentClassFactory from "../eoec/EnterpriseComponentClassFactory";

class PlatformSelect extends Component {

    constructor(props) {
        super(props);
        this.pickValues = props.appContext.sessionContext.getPickValues();
    }

    state = {
        id: uuidv4(),
        fetchBackgroundOperation: false,
        ec: null,
        buffer: [
            new Map([
                [this.props.picklist.getTypeValue(), this.props.value],
                [this.props.picklist.getTypeField(), "temp"]
            ])
        ]
    }

    componentDidMount() {
        if (this.props.picklist !== null && this.props.picklist.getStatic() === false) {
            let enterpriseComponentObject = enterpriseComponentClassFactory(this.props.picklist.getEnterpriseComponent());

            let picklistEC = new (enterpriseComponentObject)(this.props.picklist.getEnterpriseComponent());

            this.setState({ec: picklistEC});

            let searchSpecification = this.props.picklist.getSearchSpecification();
            if (searchSpecification !== null) {
                let keys = Object.keys(searchSpecification);
                for (let i = 0; i < keys.length; i++) {
                    let ss = searchSpecification[keys[i]];
                    // eslint-disable-next-line no-template-curly-in-string
                    if (ss && ss.includes("${sessionContext:realm}")) {
                        searchSpecification[keys[i]] = this.props.appContext.sessionContext.getRealm();
                    }
                }
            }
            picklistEC.addLoadStartedListener(this);
            picklistEC.addLoadCompletedListener(this);
            picklistEC.newQuery();
            picklistEC.setSearchSpecification(searchSpecification);
            picklistEC.setSortSpecification(this.props.picklist.getSortSpecification());
            picklistEC.executeQuery();
        }
    }

    enterpriseComponentLoadStarted = (_) => {
        this.setState({fetchBackgroundOperation: true});
    };

    enterpriseComponentLoadCompleted = (ec) => {
        let b = prepareBuffer(ec);
        this.setState({buffer: b, fetchBackgroundOperation: false});
    };

    // Reshape a flat option list ({value, displayValue, parentValue}) into depth-first tree order,
    // tagging each returned option with `depth` (0 = root), `isLast` (last among its siblings) and
    // `guides` — a boolean per ANCESTOR column (true = that ancestor has more siblings below, so a
    // continuous vertical line is drawn through this row). These drive the CSS tree lines in render
    // (connected ├ / └ / │ via borders, not glyphs, so there are no gaps between rows). An option is a
    // root when it has no parent value or its parent is not present in the list (e.g. a parent filtered
    // out by searchSpecification). Input order is preserved among roots and among each parent's children
    // (so an upstream A-Z sort carries through per level). Cycle-safe, and never drops an option.
    buildHierarchicalOptions(options) {
        let byValue = new Map();
        for (let i = 0; i < options.length; i++) {
            byValue.set(options[i].value, options[i]);
        }

        let childrenByParent = new Map();
        let roots = [];
        for (let i = 0; i < options.length; i++) {
            let opt = options[i];
            let parent = opt.parentValue;
            if (parent === undefined || parent === null || parent === "" || !byValue.has(parent) || parent === opt.value) {
                roots.push(opt);
            } else {
                if (!childrenByParent.has(parent)) childrenByParent.set(parent, []);
                childrenByParent.get(parent).push(opt);
            }
        }

        let ordered = [];
        let visited = new Set();
        // guides = one boolean per ancestor column to the LEFT of this node's own connector: true when
        // that ancestor still has siblings below it (draw a full-height vertical line), false otherwise.
        // isLast = whether this node is the last child among its own siblings (└ elbow vs ├ tee).
        let visit = (opt, depth, guides, isLast) => {
            if (visited.has(opt.value)) return;
            visited.add(opt.value);
            ordered.push({...opt, depth: depth, guides: guides.slice(), isLast: isLast});
            let kids = childrenByParent.get(opt.value) || [];
            // Children gain one more ancestor column representing THIS node: a line continues past them
            // only if this node has siblings below it (i.e. it is not the last child).
            let childGuides = depth > 0 ? guides.concat([!isLast]) : [];
            for (let k = 0; k < kids.length; k++) {
                visit(kids[k], depth + 1, childGuides, k === kids.length - 1);
            }
        };
        for (let r = 0; r < roots.length; r++) visit(roots[r], 0, [], r === roots.length - 1);

        // Safety net: append any option not reached (e.g. caught in a cycle) so nothing disappears.
        for (let i = 0; i < options.length; i++) {
            if (!visited.has(options[i].value)) ordered.push({...options[i], depth: 0, guides: [], isLast: true});
        }

        return ordered;
    }

    // Render the tree-line columns for one hierarchical option as full-height CSS cells (borders, not
    // glyphs) so vertical lines connect seamlessly between rows. One 20px cell per ancestor guide
    // (a centered full-height vertical line when guides[k] is true), then the node's own connector
    // cell: a vertical line from the top to the middle (└, last child) or all the way down (├), plus a
    // horizontal stub from the middle to the right that meets the label.
    renderTreeGuides(option) {
        const LINE = "1px solid #B0B4BA";
        const CELL = "20px";
        let guides = option.guides || [];
        let cells = [];

        for (let k = 0; k < guides.length; k++) {
            cells.push(
                <span key={"g" + k} style={{position: "relative", flex: "0 0 auto", width: CELL, alignSelf: "stretch"}}>
                    {guides[k]
                        ? <span style={{position: "absolute", left: "10px", top: 0, bottom: 0, borderLeft: LINE}} />
                        : null}
                </span>
            );
        }

        cells.push(
            <span key="connector" style={{position: "relative", flex: "0 0 auto", width: CELL, alignSelf: "stretch"}}>
                <span style={{position: "absolute", left: "10px", top: 0, height: option.isLast ? "50%" : "100%", borderLeft: LINE}} />
                <span style={{position: "absolute", left: "10px", top: "50%", width: "10px", borderTop: LINE}} />
            </span>
        );

        return cells;
    }

    getId() {
        if (this.props.id !== undefined && this.props.id !== null && this.props.id !== "") {
            return this.props.id;
        } else {
            return this.state.id;
        }
    }

    render() {
        let renderSpinner = false;

        let content;

        let selectOptions = [];

        if (this.props.picklist.getAllowNoSelection() === true) {
            let option = {
                value: this.props.picklist.getAllowNoSelectionValue(),
                displayValue: this.props.picklist.getAllowNoSelectionDisplayValue(),
            };
            selectOptions.push(option);
        }

        if (this.props.picklist.getStatic() === true) {
            for (let i = 0; i < this.pickValues.length; i++) {
                let pickInstance = this.pickValues[i];

                let pickTypeField = this.props.picklist.getTypeField();
                if (pickInstance[pickTypeField] !== this.props.picklist.getTypeValue()) continue;

                let option = {
                    value: pickInstance["Value"],
                    displayValue: pickInstance["Name"],
                };
                selectOptions.push(option);
            }
        } else if (this.props.picklist.getStatic() === false && this.state.fetchBackgroundOperation === true) {
            renderSpinner = true;
        } else if (this.props.picklist.getStatic() === false && this.state.fetchBackgroundOperation === false) {
            for (let i = 0; i < this.state.buffer.length; i++) {
                let pickTypeField = this.props.picklist.getTypeField();
                let pickValueField = this.props.picklist.getTypeValue();

                let optionValue = this.state.buffer[i].get(pickValueField);

                let displayValue = this.state.buffer[i].get(pickTypeField);
                // Optional secondary field appended in parens, e.g. "(Primary User: Jane Doe)".
                if (this.props.picklist.getSecondaryDisplayField && this.props.picklist.getSecondaryDisplayField()) {
                    let secondaryValue = this.state.buffer[i].get(this.props.picklist.getSecondaryDisplayField());
                    if (secondaryValue !== undefined && secondaryValue !== null && secondaryValue !== "") {
                        let secondaryLabel = this.props.picklist.getSecondaryDisplayLabel ? this.props.picklist.getSecondaryDisplayLabel() : null;
                        displayValue = secondaryLabel ? `${displayValue} (${secondaryLabel}: ${secondaryValue})` : `${displayValue} (${secondaryValue})`;
                    }
                }
                let option = {
                    value: optionValue,
                    displayValue: displayValue,
                };
                // Hierarchical picklists (e.g. PositionShared): carry each option's parent value so
                // the option list can be reshaped into an indented tree below.
                if (this.props.picklist.getHierarchical && this.props.picklist.getHierarchical()) {
                    let parentField = this.props.picklist.getHierarchyParentField();
                    option.parentValue = parentField ? this.state.buffer[i].get(parentField) : null;
                }
                selectOptions.push(option);
            }
        }

        // Indented-hierarchy ordering: reshape the flat, A-Z-sorted option list into a parent -> child
        // tree, emit it depth-first, and tag each option with box-drawing `treePrefix` glyphs. Roots
        // keep A-Z order; children keep A-Z order within their parent. Value model is unchanged.
        if (this.props.picklist.getHierarchical && this.props.picklist.getHierarchical() && selectOptions.length > 0) {
            selectOptions = this.buildHierarchicalOptions(selectOptions);
        }

        if (renderSpinner === true) {
            content = (
                <div>
                    <img width="35" height="35" src={spinner} alt="spinner" />
                </div>
            );
        } else {
            const {appContext, defaultOptions, helperText, ...rest} = this.props;
            content = (
                <TextField
                    select={true}
                    helperText={helperText}
                    {...rest}
               >
                    {selectOptions.map(option => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={option.depth ? {alignItems: "stretch", paddingTop: 0, paddingBottom: 0, minHeight: "36px"} : undefined}
                        >
                            {option.depth ? this.renderTreeGuides(option) : null}
                            <span style={option.depth ? {display: "flex", alignItems: "center", flex: "1 1 auto", minWidth: 0} : undefined}>
                                {option.displayValue}
                            </span>
                        </MenuItem>
                    ))}

                </TextField>
            );
        }

        return (
            <React.Fragment>
                {content}
            </React.Fragment>
        );
    }
}

export default withAppContext(PlatformSelect);